import { useQuery } from '@tanstack/react-query'
import { useIntersolveClient } from '@/lib/api'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alphabait/ui' // select'
import { useMemo } from 'react'

export default function IntersolveSettings(props: {
  value?: string | undefined
  onChange: (value: string) => void
  customerId?: string | undefined | null
  disabled?: boolean
}) {
  const intersolveClient = useIntersolveClient()

  const {
    data: shopsResponse,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['intersolveShops'],
    queryFn: async () => await intersolveClient.getShops.query(),
  })

  const intersolveShops = useMemo(() => {
    const shops = shopsResponse?.body
    if (!Array.isArray(shops)) return []

    return shops
      .filter(
        (shop: { customerId: string }) => shop.customerId === props.customerId,
      )
      .map((shop: { id: string; name: string; type?: string }) => ({
        value: shop.id,
        // Legacy INTERSOLVE shops are flagged "(old)" so clients know to pick
        // the newer INTERSOLVE_CLASSIC equivalent. Presentation-only — the shop
        // name in the DB and the end-user email (which uses the incentive
        // item's name, not the shop name) are unchanged.
        label: shop.type === 'INTERSOLVE' ? `${shop.name} (old)` : shop.name,
      }))
  }, [shopsResponse?.body, props.customerId])

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error loading shops</div>

  if (!props.customerId) {
    return <div className="text-sm">Please select a customer first.</div>
  } else if (intersolveShops.length === 0) {
    return <div className="text-sm">No shops available for this customer.</div>
  }

  return (
    <Select
      onValueChange={props.onChange}
      value={props.value}
      disabled={props.disabled}
    >
      <SelectTrigger>
        <SelectValue placeholder="Select a shop" />
      </SelectTrigger>
      <SelectContent className="dropdown-content">
        {intersolveShops.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
